import mapValues from 'lodash/mapValues';
import { useMemo } from 'react';
import { isPlatformSideNav } from '../helpers';
import type {
  GroupedNavItem,
  NavItems,
  Navigation,
  NavigationItem,
  NavigationItemLabelVariantEnum,
} from '../types';

const adaptGroupsWithSingleItem = (navItems: NavItems | undefined = []): NavItems =>
  navItems.map((item) => {
    if (item.type === 'GROUP' && item.items.length === 1) {
      return { ...item.items[0], icon: item.icon, name: item.name };
    }
    return item;
  });

/**
 * Remove empty groups from the navigation items
 */
export const removeEmptyGroups = <T extends GroupedNavItem | NavigationItem>(
  navItems: T[] | undefined = [],
): T[] =>
  navItems
    .map((item) => {
      if (item.type === 'GROUP') {
        const groupItem = item as GroupedNavItem;
        if ('items' in groupItem && groupItem.items) {
          return { ...groupItem, items: removeEmptyGroups(groupItem.items) } as T;
        }
      }
      return item;
    })
    .filter((item) => {
      if (item.type === 'GROUP') {
        const groupItem = item as GroupedNavItem;
        return 'items' in groupItem && groupItem.items && groupItem.items.length > 0;
      }
      return true;
    });
/**
 * Adapt the navigation to make groups with single item to be displayed ungrouped
 * For platform side Nav, dont adapt group Items with single item
 */
export const useGetSimplifiedNavigation = (
  navigation: Navigation,
  labelVariant?: NavigationItemLabelVariantEnum,
): Navigation => {
  return useMemo(() => {
    const processedNavigation: Navigation = {
      primary: removeEmptyGroups(navigation.primary),
      ...(navigation.secondary && { secondary: removeEmptyGroups(navigation.secondary) }),
    };
    if (isPlatformSideNav(labelVariant)) {
      return processedNavigation;
    }

    return mapValues(processedNavigation, adaptGroupsWithSingleItem);
  }, [navigation, labelVariant]);
};
